import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Control, Controller } from 'react-hook-form';
import { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { SingleOwnerFormData } from '@js/interfaces';

import { CorporateOwningIndividualValidation, CorporateOwningCorporateValidation } from '@js/formSettings/validations';
import { Address } from '@js/components/common/AddressOwner';


type Props = {
  register: UseFormRegister<{ owner: SingleOwnerFormData[] }>
  watch: (name?: string) => string
  setValue: UseFormSetValue<{ owner: SingleOwnerFormData[] }>
  deleteOwner: (index: number) => void
  errors: FieldErrors<{ owner: SingleOwnerFormData[] }>
  index: number
  single_owner: SingleOwnerFormData
  control: Control<{ owner: SingleOwnerFormData[] }>,
  isSingle: boolean
};

export const SingleCorporateOwner = (props: Props) => {
  const {
    register,
    watch,
    setValue,
    deleteOwner,
    errors,
    index,
    single_owner,
    control,
    isSingle,
  } = props;

  const watchIsRepresentative = watch(`owner.${index}.is_representative`) as string | boolean;
  const watchCategory = watch(`owner.${index}.category`) as string;
  const watchPostalCode = watch(`owner.${index}.postal_code`) as string;

  const today = new Date();
  const yearOptions: number[] = [];
  for (let i = 1900; i <= today.getFullYear(); i++) {
    yearOptions[i] = i;
  }
  const monthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const dateOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
  const _errors = errors.owner && errors.owner[index];

  return (
    <>
      {/* 1つ目には必ず担当者が実質的支配者か確認する  */}
      {index === 0 &&
        <Controller
          control={control}
          defaultValue={single_owner.is_representative}
          name={`owner.${index}.is_representative` as const}
          rules={{ required: '必ず選択してください' }}
          render={({ field: { onChange, value = `${single_owner.is_representative}` } }) =>

            <fieldset className="form-input__wrap">
              <legend className="form-label">
                <span className="form-label__required">必須</span>担当者が実質的支配者ですか？
                {/* 遷移先のページを用意するまで非表示にしておく
                <a className="form-label__link ml16" target="_blank" h"...https://www.jp`owner[${index}]`, -bank.japanpost.jp/kojin/sokin/koza/kj_sk_kz_furikomi_ksk.html" rel="noopener noreferrer">
                  <i className="material-icons form-label__link-icon">info</i>
                  実質的支配者とは
                </a>
              */}
              </legend>
              <div className="form-column">
                <label className="form-radio__label mr32" htmlFor="is_representative_yes">
                  <input
                    type="radio"
                    className={`form-radio__input ${_errors && _errors.is_representative && 'is-error'}`}
                    value="true"
                    onChange={(e) => onChange(`${e.target.value}`)}
                    checked={(value === 'true')}
                    id="is_representative_yes"
                  />
                  <span className="form-radio__text">はい</span>
                </label>
                <label className="form-radio__label mr32" htmlFor="is_representative_no">
                  <input
                    type="radio"
                    className={`form-radio__input ${_errors && _errors.is_representative && 'is-error'}`}
                    value="false"
                    onChange={(e) => onChange(`${e.target.value}`)}
                    checked={(value === 'false')}
                    id="is_representative_no"
                  />
                  <span className="form-radio__text">いいえ</span>
                </label>
              </div>
              <ErrorMessage
                errors={errors}
                name={`owner.${index}.is_representative`}
                render={({ message }) =>
                  <span className="form-input__error">担当者が実質的支配者かどうかは{message}</span>}
              />
            </fieldset>
          }
        />
      }

      {/* 1つ目のときは「いいえ」が選択されたら 2つ目以降は必ず表示 */}
      {((watchIsRepresentative === 'false') || (watchIsRepresentative === false) || index > 0) && (
        <>
          <h3 className="form-label form-heading form-file-block__button">
            {index === 0 ? '実質的支配者を登録' : '実質的支配者を追加'}
          </h3>
          <Controller
            control={control}
            defaultValue={single_owner.category}
            name={`owner.${index}.category` as const}
            rules={{ required: '必ず選択してください' }}

            render={({ field: { onChange, value } }) =>
              <fieldset className="form-input__wrap">
                <legend className="form-label">
                  <span className="form-label__required">必須</span>実質的支配者はどちらに該当しますか？
                </legend>
                <div className="form-column">
                  <label className="form-radio__label mr32">
                    <input
                      type="radio"
                      className={`form-radio__input ${_errors && _errors.category && 'is-error'}`}
                      value="individual"
                      onChange={(e) => onChange(e.target.value)}
                      checked={value === 'individual'}
                    />
                    <span className="form-radio__text">個人（自然人）</span>
                  </label>
                  <label className="form-radio__label mr32">
                    <input
                      type="radio"
                      className={`form-radio__input ${_errors && _errors.category && 'is-error'}`}
                      value="corporate"
                      onChange={(e) => onChange(e.target.value)}
                      checked={value === 'corporate'}
                    />
                    <span className="form-radio__text">法人</span>
                  </label>
                </div>
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.category`}
                  render={({ message }) =>
                    <span className="form-input__error">区分は{message}</span>}
                />
              </fieldset>
            }
          />

          {watchCategory === 'individual' && (
            <>
              <fieldset className="form-input__wrap">
                <legend className="form-label">
                  <span className="form-label__required">必須</span>お名前
                </legend>
                <div className="form-column">
                  <label className="form-column__item">
                    <span className="visually-hidden">姓</span>
                    <input
                      {...register(`owner.${index}.last_name_kanji` as const, CorporateOwningIndividualValidation.lastNameKanji)}
                      type="text"
                      className={`form-input ${_errors && _errors.last_name_kanji && 'is-error'}`}
                      placeholder="例）山田"
                      aria-invalid={errors ? 'true' : 'false'}
                      defaultValue={single_owner.last_name_kanji}

                    />
                  </label>
                  <label className="form-column__item">
                    <span className="visually-hidden">名</span>
                    <input
                      {...register(`owner.${index}.first_name_kanji` as const, CorporateOwningIndividualValidation.firstNameKanji)}
                      type="text"
                      className={`form-input ${_errors && _errors.first_name_kanji && 'is-error'}`}
                      placeholder="例）太郎"
                      aria-invalid={errors ? 'true' : 'false'}
                      defaultValue={single_owner.first_name_kanji}

                    />
                  </label>
                </div>
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.last_name_kanji`}
                  render={({ message }) =>
                    <span className="form-input__error">姓は{message}</span>}
                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.first_name_kanji`}
                  render={({ message }) =>
                    <span className="form-input__error">名は{message}</span>}
                />
              </fieldset>
              <fieldset className="form-input__wrap">
                <legend className="form-label">
                  <span className="form-label__required">必須</span>お名前（カタカナ）
                </legend>
                <div className="form-column">
                  <label className="form-column__item">
                    <span className="visually-hidden">姓（カタカナ）</span>
                    <input
                      {...register(`owner.${index}.last_name_kana` as const, CorporateOwningIndividualValidation.lastNameKana)}
                      type="text"
                      className={`form-input ${_errors && _errors.last_name_kana && 'is-error'}`}
                      placeholder="例）ヤマダ"
                      aria-invalid={errors ? 'true' : 'false'}
                      defaultValue={single_owner.last_name_kana}

                    />
                  </label>
                  <label className="form-column__item">
                    <span className="visually-hidden">名（カタカナ）</span>
                    <input
                      {...register(`owner.${index}.first_name_kana` as const, CorporateOwningIndividualValidation.firstNameKana)}
                      type="text"
                      className={`form-input ${_errors && _errors.first_name_kana && 'is-error'}`}
                      placeholder="例）タロウ"
                      aria-invalid={errors ? 'true' : 'false'}
                      defaultValue={single_owner.first_name_kana}

                    />
                  </label>
                </div>
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.last_name_kana`}
                  render={({ message }) =>
                    <span className="form-input__error">姓（カタカナ）は{message}</span>}
                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.first_name_kana`}
                  render={({ message }) =>
                    <span className="form-input__error">名（カタカナ）は{message}</span>}
                />
              </fieldset>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor={`owner.${index}.corporate_relationship`}>
                  <span className="form-label__required">必須</span>実質的支配者と顧客（法人）の関係性
                </label>
                <input
                  {...register(`owner.${index}.corporate_relationship` as const, CorporateOwningIndividualValidation.corporateRelationship)}
                  type="text"
                  id={`owner.${index}.corporate_relationship`}
                  className={`form-input ${_errors && _errors.corporate_relationship && 'is-error'}`}
                  placeholder="例）創業者、会長、大口債権者など"
                  defaultValue={single_owner.corporate_relationship}

                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.corporate_relationship`}
                  render={({ message }) =>
                    <span className="form-input__error">実質的支配者と顧客（法人）の関係性は{message}</span>}
                />
              </div>
              <Controller
                control={control}
                defaultValue={single_owner.gender}
                name={`owner.${index}.gender`}
                rules={CorporateOwningIndividualValidation.gender}

                render={({ field: { onChange, value } }) =>
                  <fieldset className="form-input__wrap">
                    <legend className="form-label">
                      <span className="form-label__required">必須</span>性別
                    </legend>
                    <div className="form-column">
                      <label className="form-radio__label mr32">
                        <input
                          type="radio"
                          className={`form-radio__input ${_errors && _errors.gender && 'is-error'}`}
                          value="male"
                          onChange={(e) => onChange(e.target.value)}
                          checked={value === 'male'}
                        />
                        <span className="form-radio__text">男性</span>
                      </label>
                      <label className="form-radio__label mr32">
                        <input
                          type="radio"
                          className={`form-radio__input ${_errors && _errors.gender && 'is-error'}`}
                          value="female"
                          onChange={(e) => onChange(e.target.value)}
                          checked={value === 'female'}
                        />
                        <span className="form-radio__text">女性</span>
                      </label>
                    </div>
                    <ErrorMessage
                      errors={errors}
                      name={`owner.${index}.gender`}
                      render={({ message }) =>
                        <span className="form-input__error">性別は{message}</span>}
                    />
                  </fieldset>
                }
              />
              <fieldset className="form-input__wrap">
                <legend className="form-label">
                  <span className="form-label__required">必須</span>生年月日
                </legend>
                <div className="form-column">
                  <label className="form-select__wrap form-column__select-date year">
                    <span className="visually-hidden">誕生年</span>
                    <select
                      className="form-select"
                      {...register(`owner.${index}.birthday_year` as const, CorporateOwningIndividualValidation.birthday)}
                      defaultValue={single_owner.birthday_year}
                    >
                      {yearOptions.map((year) => {
                        return <option value={year} key={year} >{year}</option>;
                      })}
                    </select>
                  </label>
                  <label className="form-select__wrap form-column__select-date">
                    <span className="visually-hidden">誕生月</span>
                    <select
                      className="form-select"
                      {...register(`owner.${index}.birthday_month` as const, CorporateOwningIndividualValidation.birthday)}
                      defaultValue={single_owner.birthday_month}
                    >
                      {monthOptions.map((month) => {
                        return <option value={month} key={month} >{month}</option>;
                      })}
                    </select>
                  </label>
                  <label className="form-select__wrap form-column__select-date">
                    <span className="visually-hidden">誕生日</span>
                    <select
                      className="form-select"
                      {...register(`owner.${index}.birthday_day` as const, CorporateOwningIndividualValidation.birthday)}
                      defaultValue={single_owner.birthday_day}
                    >
                      {dateOptions.map((day) => {
                        return <option value={day} key={day} >{day}</option>;
                      })}
                    </select>
                  </label>
                </div>
              </fieldset>
              <Address
                register={register}
                setValue={setValue}
                watchPostalCode={watchPostalCode}
                errors={errors}
                index={index}
                single_owner={single_owner}
              />
            </>
          )}
          {watchCategory === 'corporate' && (
            <>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor={`owner.${index}.name`}>
                  <span className="form-label__required">必須</span>法人名
                </label>
                <input
                  {...register(`owner.${index}.name` as const, CorporateOwningCorporateValidation.name)}
                  type="text"
                  id={`owner.${index}.name`}
                  className={`form-input ${_errors && _errors.name && 'is-error'}`}
                  placeholder="例）株式会社xx"
                  defaultValue={single_owner.name}

                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.name`}
                  render={({ message }) =>
                    <span className="form-input__error">法人名は{message}</span>}
                />
              </div>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor={`owner.${index}.name_kana`}>
                  <span className="form-label__required">必須</span>法人名（フリガナ）
                </label>
                <input
                  {...register(`owner.${index}.name_kana` as const, CorporateOwningCorporateValidation.nameKana)}
                  type="text"
                  id={`owner.${index}.name_kana`}
                  className={`form-input ${_errors && _errors.name_kana && 'is-error'}`}
                  placeholder="例）カブシキガイシャバツバツ"
                  defaultValue={single_owner.name_kana}

                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.name_kana`}
                  render={({ message }) =>
                    <span className="form-input__error">法人名（カタカナ）は{message}</span>}
                />
              </div>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor={`owner.${index}.corporate_number`}>
                  <span className="form-label__required">必須</span>法人番号
                </label>
                <input
                  {...register(`owner.${index}.corporate_number` as const, CorporateOwningCorporateValidation.corporateNumber)}
                  onWheel={(e) => e.currentTarget.blur()}
                  type="number"
                  id={`owner.${index}.corporate_number`}
                  className={`form-input ${_errors && _errors.corporate_number && 'is-error'}`}
                  placeholder="例）1234567891011"
                  defaultValue={single_owner.corporate_number}

                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.corporate_number`}
                  render={({ message }) =>
                    <span className="form-input__error">法人番号は{message}</span>}
                />
              </div>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor={`owner.${index}.corporate_relationship`}>
                  <span className="form-label__required">必須</span>実質的支配者と顧客（法人）の関係性
                </label>
                <input
                  {...register(`owner.${index}.corporate_relationship` as const, CorporateOwningCorporateValidation.corporateRelationship)}
                  type="text"
                  id={`owner.${index}.corporate_relationship`}
                  className={`form-input ${_errors && _errors.corporate_relationship && 'is-error'}`}
                  placeholder="例）創業者、会長、大口債権者など"
                  defaultValue={single_owner.corporate_relationship}

                />
                <ErrorMessage
                  errors={errors}
                  name={`owner.${index}.corporate_relationship`}
                  render={({ message }) =>
                    <span className="form-input__error">実質的支配者と顧客（法人）の関係性は{message}</span>}
                />
              </div>
              <Address
                register={register}
                setValue={setValue}
                watchPostalCode={watchPostalCode}
                errors={errors}
                index={index}
                single_owner={single_owner}
              />
            </>
          )}
        </>
      )}
      {!isSingle && (
        <button onClick={() => deleteOwner(index)} type="button" className="form-label__button mb40" >
          <i className="material-icons form-label__link-icon">remove_circle</i>
          この実質的支配者情報を削除する
        </button>
      )}
    </>
  );
};
